import { useState } from 'react'
import { motion } from 'framer-motion'
import PageHeader from '@shared/components/PageHeader'
import SEO from '@shared/components/SEO'
import Lightbox from '@shared/components/Lightbox'
import WorkflowStages from '@shared/components/WorkflowStages'
import AnimatedFlowchart from '@shared/components/AnimatedFlowchart'

const FLOWCHART = '/uploads/2026/02/arpg-peer-review-process-e1770664680588.png'

export default function EditorialWorkflow() {
    const [open, setOpen] = useState(false)

    return (
        <>
            <SEO
                title="Editorial Workflow"
                description="The editorial workflow of Indian Studies Review — from submission through screening, peer review and the editorial decision to publication."
                path="/editorial-workflow"
            />
            <PageHeader
                eyebrow="The Journal"
                title="Flowchart of Review Process"
                lede="The journey of a manuscript through Indian Studies Review — from submission and screening to peer review, decision and publication."
            />

            <section className="section">
                <div className="container-prose" style={{ maxWidth: '52rem' }}>
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                        className="prose-body"
                    >
                        <p>
                            Submitted manuscripts are reviewed by qualified reviewers and the editorial team. After
                            completion, the reviewers provide a review report for the assistance of the author to
                            revise the paper. This review report is sent to the corresponding author, asking them to
                            revise the manuscript.
                        </p>
                        <p>
                            After receiving the revised file from the author, it is forwarded to the reviewers for
                            review again. The reviewers review the manuscript again and provide the review report to
                            the Editor-in-Chief. The editors take the final decision of acceptance or rejection
                            according to the final review report. Below is the flow chart of the peer-review process
                            adopted by ISR.
                        </p>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                        className="mt-16"
                    >
                        <div className="text-center mb-10">
                            <div className="eyebrow eyebrow--center">Status Pipeline</div>
                            <h2 className="section-title mt-5">The eleven stages of review</h2>
                            <p className="lede mt-6 mx-auto" style={{ maxWidth: '40rem' }}>
                                Every manuscript moves through a transparent, eleven-stage pipeline. Authors are
                                notified by email at each step and can track the live status in the editorial portal.
                            </p>
                        </div>
                        <WorkflowStages />
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.55, delay: 0.1 }}
                        className="mt-20"
                    >
                        <div className="text-center mb-12">
                            <div className="eyebrow eyebrow--center">Peer-Review Process</div>
                            <h2 className="section-title mt-5">How a manuscript travels</h2>
                            <p className="lede mt-6 mx-auto" style={{ maxWidth: '40rem' }}>
                                From the author&rsquo;s desk through editorial screening and double-blind review
                                to open-access publication — follow the manuscript along the pipeline below.
                            </p>
                        </div>
                        <AnimatedFlowchart />
                        <p className="mt-8 text-center">
                            <button
                                type="button"
                                onClick={() => setOpen(true)}
                                className="text-[11px] uppercase font-semibold cursor-zoom-in underline underline-offset-4"
                                style={{ letterSpacing: '0.16em', color: 'var(--color-ink-400)' }}
                            >
                                View the original flowchart
                            </button>
                        </p>
                    </motion.div>

                    <p className="text-center text-[12.5px] mt-10" style={{ color: 'var(--color-ink-400)', lineHeight: 1.7 }}>
                        Indian Studies Review follows a double-blind peer-review process. Decisions at each stage are
                        communicated to the author through the editorial portal, where the current status can be tracked
                        at any time.
                    </p>
                </div>
            </section>

            <Lightbox
                items={[
                    {
                        kind: 'image',
                        src: FLOWCHART,
                        caption: 'ISR peer-review process',
                        downloadName: 'isr-peer-review-process.png'
                    }
                ]}
                openIndex={open ? 0 : null}
                onClose={() => setOpen(false)}
            />
        </>
    )
}
